<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import { ArrowLeft } from '@lucide/vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog/index';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar/index';
import { Badge } from '@/components/ui/badge/index';
import { Button } from '@/components/ui/button/index';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card/index';
import { Separator } from '@/components/ui/separator/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/index';
import { formatDateTimeByTag, formatDayByTag } from '@ja/core-ui/lib/date';
import { useApiQuery } from '@ja/portal-query';


import { useViewerContext } from '../composables/useCrudContext';
import { isViewerPathEmpty } from '../composables/useFieldVisibility';
import { useKeyboardShortcuts, type ShortcutConfig } from '../composables/useKeyboardShortcuts';
import { useRecentHistory } from '../composables/useRecentHistory';
import { useTranslatable } from '../composables/useTranslatable';
import { useViewerRealTime } from '../composables/useViewerRealTime';
import { useViewerTabs } from '../composables/useViewerTabs';
import { autoFormat, formatDate, looksLikeDate } from '../services/formatters';
import type { ActionConfig, FieldConfig, TabConfig, ViewerPageProps } from '../types';
import { edit, index } from '../wayfinder/routes';

import BlockConfigComponent from './ConfigBuilder/BlockConfig.vue';
import DurationValue from './DurationValue.vue';

defineProps<Partial<ViewerPageProps>>();
const contextProps = useViewerContext();

const { t, locale } = useI18n();
const page = usePage();

// Active model and config
const activeModel = computed(() => contextProps.model ?? '');
const activeConfig = computed(() => contextProps.config);
const { tr, trField, trBlock, trTab, trBadge, trValue } = useTranslatable(activeConfig);

// Tab rendering helpers shared with ViewerSheet (component lookup, icon
// resolution, synthetic-block wrapping for tab-level fields).
const { getViewerTabComponent, isCustomTab, getIcon, blocksForTab } = useViewerTabs();

const isMounted = ref(false);
const { trackItem } = useRecentHistory();

// Create a reactive copy of the item for real-time updates
const displayItem = ref<Record<string, unknown>>({ ...(contextProps.item as Record<string, unknown>) });

// Get current user ID for real-time updates
const currentUserId = computed(() => (page.props.auth as any)?.user?.id ?? null);

// Resolve model name for real-time subscription
const resolvedModelName = contextProps.model ?? '';

// Real-time updates subscription (only if model and id are available)
if (resolvedModelName && contextProps.id) {
    useViewerRealTime({
        modelName: resolvedModelName,
        recordId: contextProps.id,
        currentUserId: currentUserId.value,
        onUpdated: (newData) => {
            // Merge new data into display item
            Object.assign(displayItem.value, newData);
            toast.info(t('crud.realtime.record_updated'));
        },
        onDeleted: () => {
            toast.warning(t('crud.realtime.record_deleted'));
            router.visit(index({ model: resolvedModelName }).url);
        },
    });
}

onMounted(() => {
    isMounted.value = true;

    // Track this record in recent history if enabled
    const recentHistoryConfig = (activeConfig.value as any)?.recentHistory;
    if (recentHistoryConfig?.enabled !== false && contextProps.id && contextProps.item) {
        trackItem(activeModel.value, String(contextProps.id));
    }

    // Canonicalize ?tab=: if the URL value doesn't match an available tab
    // (e.g. permission-filtered out, renamed, typo), rewrite to the
    // resolved active tab so refreshes don't keep a stale param around.
    if (hasTabs.value) {
        const fromUrl = readTabFromUrl();
        if (fromUrl && !viewerTabs.value.some((tab) => tab.key === fromUrl)) {
            writeTabToUrl(activeTab.value);
        }
    } else {
        // Tabs were removed from the config — drop any leftover param.
        writeTabToUrl('');
    }
});

/** Viewer config shortcut */
const viewerConfig = computed(() => activeConfig.value?.viewer);

/** Tab configuration */
const viewerTabs = computed<TabConfig[]>(() => {
    const allTabs = viewerConfig.value?.tabs ?? [];
    const item = displayItem.value as Record<string, unknown>;

    return allTabs.filter((tab) => {
        const paths = (tab as TabConfig & { hideWhenEmpty?: string[] }).hideWhenEmpty ?? [];
        if (!Array.isArray(paths) || paths.length === 0) return true;
        return paths.some((path) => !isViewerPathEmpty(item, path));
    });
});
const hasTabs = computed(() => viewerTabs.value.length > 0);

const TAB_QUERY_KEY = 'tab';
function readTabFromUrl(): string {
    if (typeof window === 'undefined') return '';
    return new URL(window.location.href).searchParams.get(TAB_QUERY_KEY) ?? '';
}
function writeTabToUrl(tabKey: string): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (tabKey) {
        url.searchParams.set(TAB_QUERY_KEY, tabKey);
    } else {
        url.searchParams.delete(TAB_QUERY_KEY);
    }
    // Preserve Inertia's history state object — passing `{}` would blow away
    // Inertia v3's bookkeeping (page, scroll regions, version) and break
    // back/forward navigation rehydration.
    const currentState = window.history.state ?? {};
    window.history.replaceState(currentState, '', url.toString());
}

const _activeTab = ref(readTabFromUrl());
const activeTab = computed({
    get: () => {
        if (_activeTab.value && viewerTabs.value.some((t) => t.key === _activeTab.value)) return _activeTab.value;
        return viewerTabs.value[0]?.key ?? '';
    },
    set: (v: string) => {
        _activeTab.value = v;
        writeTabToUrl(v);
    },
});

/** Title and subtitle fields (auto-format dates) */
const title = computed(() => {
    const field = viewerConfig.value?.titleField ?? 'id';
    const raw = displayItem.value?.[field] ?? contextProps.id ?? '';
    return looksLikeDate(raw) ? formatDate(String(raw)) : raw;
});

const subtitle = computed(() => {
    const field = viewerConfig.value?.subtitleField;
    if (!field) return null;
    const raw = displayItem.value?.[field] ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    return looksLikeDate(raw) ? formatDate(String(raw)) : trValue(field, raw as string | number | boolean, String(raw));
});

/** Avatar from media collection */
const avatarCollection = computed(() => viewerConfig.value?.avatarCollection ?? null);
const avatarQueryKey = computed(() => ['media', activeModel.value, String(contextProps.id), avatarCollection.value]);
const avatarApiUrl = computed(
    () => `/crud/${activeModel.value}/${contextProps.id}/media?collection=${encodeURIComponent(avatarCollection.value ?? '')}`,
);

interface MediaFile { thumb_url: string; file_name: string }

const { data: avatarMedia } = useApiQuery<MediaFile[]>(
    avatarQueryKey,
    avatarApiUrl,
    { enabled: computed(() => !!avatarCollection.value && !!activeModel.value && !!contextProps.id) },
);

const avatarUrl = computed(() => avatarMedia.value?.[0]?.thumb_url ?? null);

/** Header fields */
const headerFields = computed(() => {
    return viewerConfig.value?.headerFields ?? [];
});

// Get value from item (uses displayItem for real-time updates)
function getValue(field: FieldConfig): string | number | null | undefined {
    return displayItem.value?.[field.key] ?? field.default ?? null;
}

// Format value for display
function formatValue(field: FieldConfig, value: unknown): string {
    if (value === null || value === undefined) {
        return '—';
    }

    if (field.type === 'boolean' || field.type === 'switch' || field.type === 'checkbox') {
        return value ? t('crud.yes') : t('crud.no');
    }

    if (field.type === 'date' && value) {
        return formatDayByTag(String(value), locale.value);
    }

    if (field.type === 'datetime' && value) {
        return formatDateTimeByTag(String(value), locale.value);
    }

    if (Array.isArray(value)) {
        return value.join(', ');
    }

    return String(value);
}

function formatHeaderValue(fieldKey: string): string {
    const raw = displayItem.value?.[fieldKey] ?? null;
    if (raw === null || raw === undefined || raw === '') {
        return '—';
    }

    if (looksLikeDate(raw)) {
        return formatDate(String(raw), locale.value);
    }

    return trValue(fieldKey, raw as string | number | boolean, autoFormat(raw, t('crud.yes'), t('crud.no'), locale.value));
}

/** Actions */
const actions = computed(() => viewerConfig.value?.actions ?? []);

// Keyboard shortcuts
const shortcuts = computed<ShortcutConfig[]>(() => {
    return (
        actions.value
            ?.filter((a) => a.shortcut && !a.hidden)
            .map((a) => ({
                key: a.shortcut!,
                callback: () => handleAction(a),
                description: a.name,
            })) ?? []
    );
});

useKeyboardShortcuts(shortcuts);

// Handle action execution
function handleAction(action: ActionConfig): void {
    if (action.confirm) {
        confirmAction.value = action;
        showConfirmDialog.value = true;
        return;
    }

    executeAction(action);
}

// Confirmation dialog state
const showConfirmDialog = ref(false);
const confirmAction = ref<ActionConfig | null>(null);

// Execute action
function executeAction(action: ActionConfig): void {
    if (action.navigate) {
        const url = action.navigate.replace(':id', String(contextProps.id));
        router.visit(url);
        return;
    }

    if (action.type === 'event' && action.eventName) {
        // Dispatch custom event for domain-specific logic
        window.dispatchEvent(
            new CustomEvent(action.eventName, {
                detail: { id: contextProps.id, model: activeModel.value, payload: action.eventPayload },
            }),
        );
        return;
    }

    if (action.route) {
        const url = action.route.replace(':id', String(contextProps.id));
        const method = (action.method?.toLowerCase() ?? 'post') as any;

        if (method === 'get') {
            router.get(url, {}, { preserveScroll: true });
        } else {
            router[method as 'post' | 'put' | 'patch' | 'delete'](url, {}, {
                preserveScroll: true,
            });
        }
        return;
    }

    // Default actions
    if (action.name === 'edit') {
        router.visit(edit({ model: activeModel.value, id: String(contextProps.id) }).url);
    } else if (action.name === 'delete') {
        // Handle delete
        router.delete(`/crud/${activeModel.value}/${contextProps.id}`);
    }
}
</script>

<template>
    <div class="space-y-6">
        <!-- Header -->
        <div class="flex items-start justify-between gap-4">
            <div class="flex items-center gap-4">
                <Link :href="index(activeModel)">
                    <Button variant="ghost" size="icon" class="shrink-0">
                        <ArrowLeft class="size-4" />
                    </Button>
                </Link>

                <Avatar v-if="avatarCollection" class="size-16 shrink-0">
                    <AvatarImage v-if="avatarUrl" :src="avatarUrl" />
                    <AvatarFallback class="text-lg">
                        {{ String(title).charAt(0).toUpperCase() }}
                    </AvatarFallback>
                </Avatar>

                <div class="min-w-0">
                    <h1 class="text-2xl font-bold tracking-tight">
                        {{ title }}
                    </h1>
                    <p v-if="subtitle" class="text-muted-foreground mt-0.5">
                        {{ subtitle }}
                    </p>
                    <!-- Inline metadata -->
                    <div v-if="headerFields.length > 0" class="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                        <div v-for="fieldKey in headerFields" :key="fieldKey" class="flex items-center gap-1.5">
                            <span class="font-medium">{{ trField(fieldKey) }}:</span>
                            <span>{{ formatHeaderValue(fieldKey) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex shrink-0 items-center gap-2">
                <Button
                    v-for="action in actions.filter((a) => !a.hidden)"
                    :key="action.name"
                    :variant="(action.color as any) ?? 'default'"
                    @click="handleAction(action)"
                >
                    <component v-if="action.icon" :is="action.icon" class="mr-2 size-4" />
                    {{ t(`crud.actions.${action.name}`) }}
                </Button>
            </div>
        </div>

        <Separator />

        <!-- Tabbed layout -->
        <Tabs v-if="viewerConfig && hasTabs" v-model="activeTab" class="space-y-4">
            <TabsList class="max-w-full justify-start gap-1 overflow-x-auto">
                <TabsTrigger v-for="tab in viewerTabs" :key="tab.key" :value="tab.key" class="shrink-0 gap-2 whitespace-nowrap">
                    <component v-if="tab.icon && getIcon(tab.icon)" :is="getIcon(tab.icon)" class="size-4" />
                    {{ trTab(tab.key) }}
                </TabsTrigger>
            </TabsList>

            <TabsContent
                v-for="tab in viewerTabs"
                :key="tab.key"
                :value="tab.key"
                class="space-y-6"
            >
                <!-- Custom tab component -->
                <component
                    v-if="isCustomTab(tab) && getViewerTabComponent(tab.component!)"
                    :is="getViewerTabComponent(tab.component!)!"
                    :tab="tab"
                    :item="displayItem"
                    :model="activeModel"
                    :item-id="contextProps.id"
                    :tr-field="trField"
                    :tr-tab="trTab"
                    :tr-block="trBlock"
                    :translation-prefix="activeConfig?.translationPrefix ?? null"
                    :component-props="tab.componentProps ?? undefined"
                />
                <!-- Standard blocks inside tab. Tab-level layout overrides the viewer-level layout. -->
                <div
                    v-else
                    :class="{
                        'grid gap-6': true,
                        'md:grid-cols-1': (tab.layout ?? viewerConfig.layout) === 'single-column',
                        'md:grid-cols-2': (tab.layout ?? viewerConfig.layout) === 'two-column',
                    }"
                >
                    <BlockConfigComponent
                        v-for="block in blocksForTab(tab)"
                        :key="block.key"
                        :block="block"
                        :form="displayItem"
                        :errors="{}"
                        :tr-field="trField"
                        :tr-block="trBlock"
                        :translation-prefix="activeConfig?.translationPrefix"
                        :item="displayItem"
                        :model="activeModel"
                        :item-id="contextProps.id"
                    />
                    <p v-if="blocksForTab(tab).length === 0" class="text-muted-foreground col-span-full text-sm italic">
                        {{ t('crud.empty_tab') }}
                    </p>
                </div>
            </TabsContent>
        </Tabs>

        <!-- Flat blocks layout -->
        <div
            v-else-if="viewerConfig"
            :class="{
                'grid gap-6': true,
                'md:grid-cols-1': viewerConfig.layout === 'single-column',
                'md:grid-cols-2': viewerConfig.layout === 'two-column',
            }"
        >
            <BlockConfigComponent
                v-for="block in viewerConfig.blocks"
                :key="block.key"
                :block="block"
                :form="displayItem"
                :errors="{}"
                :tr-field="trField"
                :tr-block="trBlock"
                :translation-prefix="activeConfig?.translationPrefix"
                :item="displayItem"
                :model="activeModel"
                :item-id="contextProps.id"
            />
        </div>

        <!-- Fallback to editor tabs if no viewer config -->
        <div v-else-if="activeConfig?.editor" class="space-y-6">
            <Card v-for="tab in activeConfig.editor.tabs" :key="tab.key">
                <CardHeader>
                    <CardTitle>{{ tr(`tabs.${tab.key}`, tab.label) }}</CardTitle>
                </CardHeader>
                <CardContent>
                    <dl class="grid gap-4 sm:grid-cols-2">
                        <div v-for="field in tab.fields" :key="field.key" class="space-y-1">
                            <dt class="text-muted-foreground text-sm font-medium">
                                {{ trField(field.key, field.label) }}
                            </dt>
                            <dd class="text-sm">
                                <Badge v-if="field.type === 'badge'" :variant="trBadge(getValue(field), 'default') as any">
                                    {{ (field.options as any[])?.find((o) => String(o.value) === String(getValue(field)))?.label ?? getValue(field) }}
                                </Badge>
                                <DurationValue
                                    v-else-if="field.type === 'duration'"
                                    :model-value="getValue(field) as number | null"
                                    :default-mode="(field.metadata?.defaultMode as 'hhmm' | 'decimal' | 'minutes' | undefined) ?? 'hhmm'"
                                />
                                <span v-else>
                                    {{ formatValue(field, getValue(field)) }}
                                </span>
                            </dd>
                        </div>
                    </dl>
                </CardContent>
            </Card>
        </div>

        <!-- Confirmation dialog -->
        <AlertDialog v-model:open="showConfirmDialog">
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {{ confirmAction?.confirmTitle ?? t('crud.confirm_action', { action: confirmAction?.name }) }}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {{ confirmAction?.confirmMessage ?? t('crud.confirm_action_message', { action: confirmAction?.name }) }}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{{ t('crud.cancel') }}</AlertDialogCancel>
                    <AlertDialogAction
                        @click="
                            confirmAction && executeAction(confirmAction);
                            showConfirmDialog = false;
                        "
                    >
                        {{ t('crud.confirm') }}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </div>
</template>
